import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Column, Container, Icon, List, Row, VideoModal } from '@pella/sash-framework';
import { Image, CTA } from '../../../';
import { isValidArray } from '../../../utilities/validations';

import ResultsTile from '../../../../templates/plp/results/tile';

import styles from './index.module.scss';

const PDPTabs = ({ data, proIcon, configUrl, className, pageUrl, ...other }) => {
	const [activeTab, setActiveTab] = useState(0);
	const [videoModal, setVideoModal] = useState(null);
	const [activeSubTab, setActiveSubTab] = useState(0);
	const [showMobileTabs, setShowMobileTabs] = useState(false);

	if (!data) return null;

	const renderProTips = (data, index) => (
		<div
			key={`proTips-${index}`}
			className={styles.pdpDesign_ProTipsWrapper}
		>
			{proIcon && (
				<Image
					data={{ image: { url: proIcon }, image_alt: '' }}
					className={styles.pdpDesign_ProTipsIcon}
				/>
			)}

			{data.pro_tips.heading && <p className={styles.pdpDesign_ProTipsTitle}>{data.pro_tips.heading}</p>}

			{data.pro_tips.description && (
				<div
					dangerouslySetInnerHTML={{ __html: data.pro_tips.description }}
					className={styles.pdpDesign_ProTipsDescription}
				/>
			)}
		</div>
	);

	const onTabClick = (index) => {
		setActiveTab(index);
		setShowMobileTabs(false);
	};

	const onSubTabClick = (i) => setActiveSubTab(i);

	const onVideoBtnClick = (value) => setVideoModal(value);

	const onVideoCloseClick = () => setVideoModal(null);

	const showDuration = (video) => {
		if (!video.schema?.duration) return null;

		const duration = video.schema.duration;
		const splitDuration = duration.split('PT')[1].split('M');
		const minute = splitDuration[0];
		const seconds = splitDuration[1].split('S')[0];

		return (
			<span
				className='ml-1 align-[unset]'
				data-testid='pdpTabs-duration'
			>
				({minute}:{seconds})
			</span>
		);
	};

	const onMobileTabsBtnClick = () => setShowMobileTabs(!showMobileTabs);

	const renderImageBadges = (badges) => (
		<List
			bullets='none'
			layout='inline'
			className='absolute left-4 top-2 z-2 space-x-1 md:left-2'
		>
			{badges.map((badge, index) => {
				if (!badge.color || !badge.badge) return null;

				return (
					<li key={`badge-${index}`}>
						<Badge
							color={badge.color}
							dangerouslySetInnerHTML={{ __html: badge.badge }}
							className='!border-0 !px-1 !pb-1'
							size='md'
						/>
					</li>
				);
			})}
		</List>
	);

	return (
		<div
			id={data.anchor_text}
			className={className || ''}
			data-testid='pdp-tabs'
			{...other}
		>
			<Container>
				{data.heading && <h2 className='mb-8 text-center'>{data.heading}</h2>}

				<div className={`${styles.pdpDesign_TabsWrapper} relative`}>
					<button
						onClick={onMobileTabsBtnClick}
						className='pdp-tabs-mobile-btn flex w-full items-center bg-black px-2.5 py-3 text-center text-sm uppercase tracking-2px text-white xl:hidden'
						data-testid='pdp-tabs-mobile-btn'
					>
						<span className='pdp-tabs-mobile-text flex-1'>{data.options_and_swatches?.tabs?.[activeTab]?.tab_name}</span>
						<Icon
							name='keyboard_arrow_down'
							className='flex-initial'
						/>
					</button>

					<List
						bullets='none'
						className={`pdp-tabs-list ${styles.pdpDesign_Tabs} absolute z-2 w-full bg-white md:relative md:bg-neutral-200 xl:flex ${showMobileTabs ? '' : 'hidden'}`}
						data-testid='pdp-tabs-tab-list'
					>
						{data.options_and_swatches.tabs.map((tab, index) => (
							<li
								key={`tabs-${index}`}
								className={`pdp-tab-${tab?.tab_name?.replace(/\s+/g, '-').toLowerCase()}`}
							>
								<button
									className={activeTab === index ? `pdpTabs-active ${styles.pdpDesign_Button_active}` : ''}
									onClick={() => onTabClick(index)}
								>
									<span className={styles.pdpDesign_TabsText}>{tab.tab_name}</span>
								</button>
							</li>
						))}
					</List>

					<div className='pdp-tabs-content bg-white'>
						{data.options_and_swatches.tabs.map((panels, index) => (
							<React.Fragment key={`panels-${index}`}>
								<div className={`p-5 sm:p-7 pdp-tabs-content-${panels.tab_name?.replace(/\s+/g, '-').toLowerCase()} ${activeTab === index ? `pdpTabs-activePanel` : 'hidden'}`}>
									{panels.tab_content && (
										<div
											dangerouslySetInnerHTML={{ __html: panels.tab_content }}
											className='mb-8 max-w-[100ch] text-sm sm:text-base xl:text-xl'
										/>
									)}

									{isValidArray(panels?.swatches) &&
										!panels.hardware_tabs &&
										panels.swatches.map((panel, index) => (
											<div
												key={`swatches-${index}`}
												className={index > 0 ? 'mt-8' : ''}
												data-testid='swatches-panel'
											>
												<Row
													align='center'
													verticalGutters={true}
												>
													<Column
														xs={12}
														md='fluid'
													>
														{panel.special_type !== 'single image' && panel.options_title && <p className='mb-2.5 text-xl font-semibold lg:text-2xl'>{panel.options_title}</p>}

														{panel.special_type !== 'single image' && panel.description && (
															<div
																className='max-w-full'
																dangerouslySetInnerHTML={{ __html: panel.description }}
															/>
														)}
													</Column>

													{panel.video?.[0] && (
														<Column
															xs={12}
															md='auto'
														>
															<Button
																color='primary'
																onClick={() => onVideoBtnClick(panel.video[0].video?.video_id)}
																className='inline-flex items-center'
															>
																<Icon
																	name='play_circle'
																	size={30}
																/>
																<span>
																	{panel.video[0].video.button_title}
																	{showDuration(panel.video[0])}
																</span>
															</Button>

															<VideoModal
																isOpen={videoModal === panel.video[0].video.video_id}
																onClose={() => onVideoCloseClick(null)}
																videoId={panel.video[0].video.video_id}
																title={panel.video[0].video.title}
																transcript={panel.video[0].transcript}
															/>
														</Column>
													)}
												</Row>

												{isValidArray(panel?.options_images) && (
													<List
														bullets='none'
														className={styles.pdpDesign_OptionsList}
														aria-label='Options List'
													>
														{panel.options_images.map((image, index) => (
															<li
																key={`option-${index}`}
																className={`relative ${panel.options_images.length < 9 ? styles.pdpDesign_OptionsListItem : ''} ${panel.options_title === 'Combinations' ? styles.pdpDesign_OptionsFixedHeight : ''}`}
															>
																{isValidArray(image.badge) && renderImageBadges(image.badge)}

																<Image
																	data={image}
																	className={styles.pdpDesign_OptionImage}
																	objectFit='scale-down'
																/>

																{image.image_title && <p className={styles.pdpDesign_OptionName}>{image.image_title}</p>}
															</li>
														))}
													</List>
												)}

												{panel.special_type === 'screens' && isValidArray(panel?.screen_types) && (
													<Row
														justify='center'
														className={styles.pdpDesign_OptionsScreens}
													>
														{panel.screen_types.map((screen, index) => (
															<Column
																key={`screens-${index}`}
																xs={12}
																sm={6}
																lg={4}
															>
																{screen.file?.url && <Image data={{ image: screen.file }} />}
																{screen.heading && <p className={styles.pdpDesogn_OptionsScreensHeading}>{screen.heading}</p>}
																{screen.description && <div dangerouslySetInnerHTML={{ __html: screen.description }} />}
															</Column>
														))}
													</Row>
												)}

												{panel.special_type === 'single image' && (
													<Row
														noGutters={true}
														align='center'
														className='text-left'
													>
														{isValidArray(panel.single_image) && (
															<Column
																xs={12}
																sm={4}
															>
																<Image
																	data={panel.single_image[0]}
																	className='!block w-full border border-neutral-200 p-10'
																/>
															</Column>
														)}
														<Column
															xs={12}
															sm={8}
														>
															{panel.options_title && <p className='sm:pl-7 xl:pl-12'>{panel.options_title}</p>}
															{panel.description && (
																<div
																	className='mx-0 sm:pl-7 xl:pl-12'
																	dangerouslySetInnerHTML={{ __html: panel.description }}
																/>
															)}
														</Column>
													</Row>
												)}

												{panel.special_type === 'package' && isValidArray(panel.screen_types) && (
													<Row verticalGutters={true}>
														{panel.screen_types.map((item, index) => (
															<Column
																key={`package-${index}`}
																xs={12}
																md={6}
															>
																<div className={styles.pdpDesign_Package}>
																	<Row align='center'>
																		{item.file?.url && (
																			<Column xs='auto'>
																				<Image
																					data={{ image: item.file }}
																					className={styles.pdpDesign_PackageImage}
																				/>
																			</Column>
																		)}
																		<Column xs='fluid'>
																			{item.heading && <p className={styles.pdpDesign_PackageHeading}>{item.heading}</p>}
																			{item.description && (
																				<div
																					dangerouslySetInnerHTML={{ __html: item.description }}
																					className={styles.pdpDesign_PackageDescription}
																				/>
																			)}
																		</Column>
																	</Row>
																</div>
															</Column>
														))}
													</Row>
												)}

												{panel.combinations?.group?.[0] && (
													<Row
														className={`${styles.combinations} ${panel.combinations.group.length > 1 ? '' : styles.combinations_noWrap}`}
														justify='center'
														noGutters
													>
														{panel.combinations.group.map((group, index) => (
															<Column
																xs={12}
																md={group.tile?.length > 1 ? 12 : 'auto'}
																xl='auto'
																key={`combos-${index}`}
															>
																{group.heading && <p className='mb-2 text-center text-base uppercase tracking-2px md:text-lg'>{group.heading}</p>}

																{isValidArray(group.tile) && (
																	<div
																		className={`md:justify-center ${group.tile?.length > 1 ? 'max-md:space-y-4 md:grid md:grid-cols-[245px_245px] md:gap-x-4 xl:grid-cols-2' : 'md:flex md:flex-row'}`}
																		data-testid='pdp-tabs-tiles'
																	>
																		{group.tile.map((tile, index) => (
																			<ResultsTile
																				key={`tile-${index}`}
																				tile={tile}
																				pdpURL={pageUrl === tile.static_url ? configUrl : tile.static_url}
																				className={`${styles.combinations_Tile}`}
																				btnText={pageUrl === tile.static_url ? 'Build & Add To Cart' : 'View Details'}
																			/>
																		))}
																	</div>
																)}

																{isValidArray(group.other) && (
																	<div className='!m-0 px-6 py-4 xl:px-8'>
																		<div className='flex flex-row flex-wrap justify-center text-center'>
																			{group.other.map((combo, index) => (
																				<div
																					className={`${styles.combinations_Combo} md:p-2 xl:p-4 ${index < group.other.length - 1 ? 'mb-8 md:mb-0' : ''}`}
																					key={`other-${index}`}
																				>
																					<Image data={combo} />
																					<p className='mt-1 font-semibold'>{combo.image_title}</p>
																				</div>
																			))}
																		</div>

																		{isValidArray(group.cta) && (
																			<CTA
																				className='mt-8'
																				data={group.cta[0]}
																				size='sm'
																				wide
																			/>
																		)}
																	</div>
																)}
															</Column>
														))}
													</Row>
												)}

												{panel.pro_tips && (panel.pro_tips.heading || panel.pro_tips.description) && renderProTips(panel)}
											</div>
										))}

									{isValidArray(panels?.swatches) && panels.hardware_tabs && (
										<div
											key={`subtabs-${index}`}
											className={index > 0 ? 'mt-8' : ''}
											data-testid='swatches-subpanel'
										>
											<List
												bullets='none'
												className='mb-8 flex flex-row flex-nowrap overflow-auto border-b border-b-neutral-200 md:justify-center'
												aria-label='Sub Tabs List'
											>
												{panels.swatches.map((panel, index) => (
													<li
														key={`subtab-${index}`}
														className={`relative flex-none ${index > 0 ? 'ml-5 pl-5 before:absolute before:bottom-4 before:left-0 before:top-4 before:w-px before:bg-neutral-200 before:content-[""] md:ml-10 md:pl-10' : ''}`}
													>
														<button
															aria-label={panel.options_title}
															onClick={() => onSubTabClick(index)}
															className={`py-4 font-semibold ${index == activeSubTab ? 'pdpTabs-activeSubTab text-black shadow-[inset_0_-4px_0_#ffde00]' : 'text-neutral'}`}
														>
															{panel.options_title}
														</button>
													</li>
												))}
											</List>

											{panels.swatches.map((panel, index) => (
												<div
													key={`subtabs-panel-${index}`}
													className={index == activeSubTab ? 'pdpTabs-activeSubPanel-active' : 'hidden'}
												>
													{panel.options_title && <p className='mb-2.5 text-xl font-semibold lg:text-2xl'>{panel.options_title}</p>}
													{panel.description && (
														<div
															className={styles.pdpDesign_TabsPanelDescription}
															dangerouslySetInnerHTML={{ __html: panel.description }}
														/>
													)}

													{isValidArray(panel?.options_images) && (
														<List
															bullets='none'
															className={styles.pdpDesign_OptionsList}
														>
															{panel.options_images.map((image, index) => (
																<li
																	key={`subtabs-options-${index}`}
																	className={`relative ${panel.options_images.length < 9 ? styles.pdpDesign_OptionsListItem : ''}`}
																>
																	{isValidArray(image.badge) && renderImageBadges(image.badge)}

																	<Image
																		data={image}
																		className={styles.pdpDesign_OptionImage}
																	/>

																	{image.image_title && <p className={styles.pdpDesign_OptionName}>{image.image_title}</p>}
																</li>
															))}
														</List>
													)}
												</div>
											))}

											{panels.swatches.map((panel) => panel.pro_tips && (panel.pro_tips.heading || panel.pro_tips.description) && renderProTips(panel, index))}
										</div>
									)}
								</div>
							</React.Fragment>
						))}
					</div>
				</div>
			</Container>
		</div>
	);
};

PDPTabs.propTypes = {
	data: PropTypes.object,
	proIcon: PropTypes.string,
	configUrl: PropTypes.string,
	className: PropTypes.string,
	pageUrl: PropTypes.string,
};

export default PDPTabs;
